import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { workflowService } from '../services/workflow/workflowService';
import {
  WorkflowProvider,
  useWorkflow,
  STEP_LABELS,
} from '../contexts/WorkflowContext';
import { HelpLink, WorkflowStepId } from '../components/common/HelpLink';
import StepNavigation from '../components/workflow/StepNavigation';
import StepContainer from '../components/workflow/StepContainer';
import StepActions from '../components/workflow/StepActions';
import LearningPanel from '../components/learning/LearningPanel';

// Step Components
import DiscoveryStep from '../components/steps/DiscoveryStep/DiscoveryStep';
import DesignStep from '../components/steps/DesignStep/DesignStep';
import MultiAgentStep from '../components/steps/MultiAgentStep/MultiAgentStep';
import TechnicalStep from '../components/steps/TechnicalStep/TechnicalStep';
import CodeGenStep from '../components/steps/CodeGenStep/CodeGenStep';
import OperationsStep from '../components/steps/OperationsStep/OperationsStep';

const WorkflowContent: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const {
    currentStep,
    learningMode,
    toggleLearningMode,
    stepStatuses,
    workflowComplete,
    setProjectId,
    loadFullProjectData,
    isDataLoaded,
    isLoading,
    projectTitle,
    getStepData,
  } = useWorkflow();

  // Set project ID from URL params and load full project data for resume
  useEffect(() => {
    if (projectId) {
      setProjectId(projectId);
      // Load full project data including chat history and spec data
      loadFullProjectData().catch(console.error);
    }
  }, [projectId, setProjectId, loadFullProjectData]);

  const [isDownloadingDoc, setIsDownloadingDoc] = useState(false);

  const handleDownloadMasterDocument = useCallback(async () => {
    if (!projectId) return;
    setIsDownloadingDoc(true);
    try {
      const blob = await workflowService.downloadMasterDocument(projectId, 'pdf');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(projectTitle || 'agent-design').replace(/\s+/g, '_')}_master.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download master document:', err);
    } finally {
      setIsDownloadingDoc(false);
    }
  }, [projectId, projectTitle]);

  // Get step data for current step to pass to components
  const currentStepData = getStepData(currentStep);

  const renderStep = () => {
    // Pass loaded step data to each step component for resume functionality
    const stepProps = {
      initialChatHistory: currentStepData.chatHistory,
      initialSpecData: currentStepData.specData,
      initialQualityScore: currentStepData.qualityScore,
      initialQualityReport: currentStepData.qualityReport,
    };

    switch (currentStep) {
      case 'discovery':
        return <DiscoveryStep {...stepProps} />;
      case 'design':
        return <DesignStep {...stepProps} />;
      case 'multiagent':
        return <MultiAgentStep {...stepProps} />;
      case 'technical':
        return <TechnicalStep {...stepProps} />;
      case 'codegen':
        return <CodeGenStep {...stepProps} />;
      case 'operations':
        return <OperationsStep {...stepProps} />;
      default:
        return <DiscoveryStep {...stepProps} />;
    }
  };

  // Show loading state while fetching project data
  if (isLoading && !isDataLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading project...</Typography>
      </Box>
    );
  }

  // Get status chip for current step
  const getStepStatusChip = () => {
    const status = stepStatuses[currentStep];
    switch (status) {
      case 'approved':
        return <Chip label="Approved" color="success" size="small" />;
      case 'skipped':
        return <Chip label="Skipped" color="warning" size="small" />;
      case 'in_progress':
        return <Chip label="In Progress" color="info" size="small" />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">
            {projectTitle || 'Agent Project'}
          </Typography>
          {workflowComplete && <Chip label="Complete" color="success" />}
          <Tooltip title="Download Master Document (PDF)">
            <span>
              <IconButton
                size="small"
                onClick={handleDownloadMasterDocument}
                disabled={isDownloadingDoc}
              >
                {isDownloadingDoc ? (
                  <CircularProgress size={20} />
                ) : (
                  <PdfIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <FormControlLabel
          control={
            <Switch checked={learningMode} onChange={toggleLearningMode} />
          }
          label="Learning Mode"
        />
      </Box>

      {/* Step Navigation */}
      <StepNavigation />

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexGrow: 1, position: 'relative' }}>
        <Box
          sx={{
            flexGrow: 1,
            mr: learningMode ? '300px' : 0,
            transition: 'margin 0.3s',
          }}
        >
          {/* Current Step Header */}
          <Box
            sx={{
              px: 4,
              pt: 2,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Step: {STEP_LABELS[currentStep]}
            </Typography>
            <HelpLink step={currentStep as WorkflowStepId} />
            {getStepStatusChip()}
          </Box>

          {/* Step Content */}
          <StepContainer>{renderStep()}</StepContainer>

          {/* Step Actions (Approve/Skip/Revisit) */}
          <Box sx={{ px: 4, pb: 4 }}>
            <StepActions />
          </Box>
        </Box>

        {/* Learning Panel (sidebar) */}
        <LearningPanel />
      </Box>
    </Box>
  );
};

const AgentProjectPage: React.FC = () => {
  return (
    <WorkflowProvider>
      <WorkflowContent />
    </WorkflowProvider>
  );
};

export default AgentProjectPage;
